'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { BookOpenIcon, ShuffleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useMemo, useState } from 'react'

import { Response } from '@/components/elements/response'
import { SparkleIcon, TrendIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { useOpenArbDialog } from '@/features/arb/hooks/use-open-arb-dialog'
import { useDashboardCommands } from '@/features/dashboard/hooks/use-dashboard-commands'
import { getHedgePagePath } from '@/features/dashboard/lib/trade-page-path'
import {
  HOME_EASE,
  HOME_LIST,
  HOME_ROW,
  HOME_SOFT_SPRING,
  HomePanel,
} from '@/features/home/components/home-motion'
import { HomePlaybookDialog } from '@/features/home/components/home-playbook'
import { type ChartLevel, HomePriceChart } from '@/features/home/components/home-price-chart'
import type { HomeDailyBrief } from '@/features/home/hooks/use-home-daily-brief'
import {
  describeIdea,
  type IdeaBucket,
  type ReactiveKind,
  type TradeIdea,
} from '@/features/home/lib/home-ideas'
import { liveSetups, type OpportunityCategory } from '@/features/home/lib/home-opportunities'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { useBatchOrderPreview } from '@/service/order-preview-batch.hook'
import { OrderType } from '@/types/generated/common/trading'

const KIND_TONE: Record<ReactiveKind, string> = {
  reduce: 'bg-chart-red/10 text-chart-red',
  stopLoss: 'bg-ox-gold/10 text-ox-gold',
  expiring: 'bg-ox-gold/10 text-ox-gold',
  funding: 'bg-ox-gold/10 text-ox-gold',
  takeProfit: 'bg-chart-mint/10 text-chart-mint',
  hedge: 'bg-shell-accent/10 text-shell-accent',
  firstTrade: 'bg-shell-accent/10 text-shell-accent',
}

export const CATEGORY_TONE: Record<OpportunityCategory, string> = {
  momentum: 'bg-chart-mint/10 text-chart-mint',
  meanReversion: 'bg-ox-gold/10 text-ox-gold',
  arbitrage: 'bg-ox-cyan/10 text-ox-cyan',
  fundamental: 'bg-shell-accent/10 text-shell-accent',
  news: 'bg-foreground/10 text-foreground/70',
}

/**
 * What the idea's order would do, from the server's order preview: the liquidation price and
 * leverage now and after it fills — the "what you're signing up for" line a ticket shows.
 */
function IdeaPreview({ idea }: { idea: TradeIdea }) {
  const t = useTranslations('trade.home.ideas.preview')
  const fmt = useNumberFormat()
  const preview = idea.preview!
  const request = useMemo(
    () => ({
      orders: [
        {
          accountId: preview.accountId,
          symbol: idea.symbol,
          side: preview.side,
          quantity: preview.quantity,
          orderType: OrderType.ORDER_TYPE_MARKET,
          denomination: preview.denomination,
        },
      ],
    }),
    [idea.symbol, preview]
  )
  const { data, isPending } = useBatchOrderPreview(request)
  const result = data?.rows[0]?.ok
  const price = (value?: string) =>
    value && Number(value) > 0 ? fmt.number(value, { maximumSignificantDigits: 6 }) : '—'
  const leverage = (value?: string) =>
    value && Number.isFinite(Number(value))
      ? `${fmt.number(value, { maximumFractionDigits: 1 })}x`
      : '—'

  if (isPending) return <span className="bg-foreground/5 h-9 rounded-sm" />
  if (!result) return null
  return (
    <motion.dl
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={HOME_SOFT_SPRING}
      className="bg-foreground/[0.04] grid grid-cols-2 gap-2 rounded-sm px-2.5 py-2 text-[11px]"
    >
      <div className="flex flex-col gap-0.5">
        <dt className="text-foreground/45">{t('liquidation')}</dt>
        <dd className="text-foreground flex items-center gap-1">
          <span className="text-foreground/50">
            {price(result.estimatedLiquidationPriceCurrent)}
          </span>
          <span aria-hidden="true">→</span>
          <span className="text-chart-mint">{price(result.estimatedLiquidationPriceAfter)}</span>
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-foreground/45">{t('leverage')}</dt>
        <dd className="text-foreground flex items-center gap-1">
          <span className="text-foreground/50">{leverage(result.grossLeverageCurrent)}</span>
          <span aria-hidden="true">→</span>
          <span className="text-chart-mint">{leverage(result.grossLeverageAfter)}</span>
        </dd>
      </div>
      {result.approximate && (
        <p className="text-foreground/35 col-span-2 text-[10px]">{t('approximate')}</p>
      )}
    </motion.dl>
  )
}

/** An arbitrage idea's picture: annual funding on each venue, the two legs marked. */
function VenueFundingBars({ idea }: { idea: TradeIdea }) {
  const t = useTranslations('trade.home.ideas')
  const fmt = useNumberFormat()
  const venues = idea.venueFunding ?? []
  const peak = Math.max(1, ...venues.map((venue) => Math.abs(venue.apr)))
  return (
    <div
      className="flex h-32 flex-col justify-center gap-1.5"
      role="img"
      aria-label={t('venueFunding')}
    >
      <span className="text-foreground/40 text-[10px]">{t('venueFunding')}</span>
      {venues.map((venue, index) => {
        const leg =
          venue.exchange === idea.arb?.short.exchange && venue.symbol === idea.arb.short.symbol
            ? 'short'
            : venue.exchange === idea.arb?.long.exchange && venue.symbol === idea.arb.long.symbol
              ? 'long'
              : null
        return (
          <div
            key={`${venue.exchange}:${venue.symbol}`}
            className="grid grid-cols-[5.5rem_minmax(0,1fr)_3.5rem] items-center gap-2 text-[11px]"
          >
            <span className="text-foreground/70 flex min-w-0 items-center gap-1.5 truncate">
              <ExchangeIcon
                exchange={getExchangeIconKey(venue.exchange) ?? ''}
                className="size-3.5"
              />
              {getExchangeLabel(venue.exchange)}
            </span>
            <span className="bg-foreground/5 relative h-2 overflow-hidden rounded-full">
              <motion.span
                className={cn(
                  'absolute inset-y-0 left-0 rounded-full',
                  leg === 'short'
                    ? 'bg-chart-red/70'
                    : leg === 'long'
                      ? 'bg-chart-mint/70'
                      : 'bg-foreground/25'
                )}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(4, (Math.abs(venue.apr) / peak) * 100)}%` }}
                transition={{ ...HOME_SOFT_SPRING, delay: index * 0.05 }}
              />
            </span>
            <span className="text-foreground text-right">
              {fmt.number(venue.apr, { maximumFractionDigits: 1, signDisplay: 'exceptZero' })}%
            </span>
          </div>
        )
      })}
    </div>
  )
}

/** The reason's wording; a funding arb reads differently when a leg is paid negative funding. */
function reasonKey(idea: TradeIdea): string {
  if (idea.kind === 'fundingArb') {
    const high = Number(idea.params.highApr)
    const low = Number(idea.params.lowApr)
    if (high < 0) return 'reasons.fundingArbLongs'
    if (low < 0) return 'reasons.fundingArbBoth'
  }
  return `reasons.${idea.kind}`
}

function IdeaCard({ idea, brief }: { idea: TradeIdea; brief: HomeDailyBrief }) {
  const t = useTranslations('trade.home.ideas')
  const fmt = useNumberFormat()
  const router = useRouter()
  const openArb = useOpenArbDialog()
  const { startTradePrompt, startResearchPrompt } = useDashboardCommands()
  const opportunity = idea.bucket === 'opportunity' && idea.category !== undefined
  const reasoning = opportunity ? null : brief.ai.reasoningFor(idea)

  // Words inside the messages that need translating, and money in the user's format.
  const values: Record<string, string | number> = { ...idea.params }
  if ('side' in idea.params) values.side = t(`sideWords.${idea.params.side}`)
  if ('crowd' in idea.params) values.crowd = t(`crowds.${idea.params.crowd}`)
  if ('direction' in idea.params) values.direction = t(`directions.${idea.params.direction}`)
  if ('notional' in idea.params) values.notional = fmt.usd(Number(idea.params.notional))
  for (const key of ['total', 'value'] as const) {
    if (key in idea.params) {
      values[key] = fmt.number(Math.abs(Number(idea.params[key])), {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      })
    }
  }

  const levels: ChartLevel[] = [
    ...(idea.levels.entry
      ? [
          {
            label: t(opportunity ? 'levels.now' : 'levels.entry'),
            price: idea.levels.entry,
            tone: 'entry' as const,
          },
        ]
      : []),
    ...(idea.levels.liquidation
      ? [
          {
            label: t('levels.liquidation'),
            price: idea.levels.liquidation,
            tone: 'danger' as const,
          },
        ]
      : []),
    ...(idea.levels.stop
      ? [{ label: t('levels.stop'), price: idea.levels.stop, tone: 'warn' as const }]
      : []),
    ...(idea.levels.target
      ? [{ label: t('levels.target'), price: idea.levels.target, tone: 'target' as const }]
      : []),
  ]

  return (
    <motion.li
      variants={HOME_ROW}
      whileHover={{ y: -3 }}
      transition={HOME_SOFT_SPRING}
      className="bg-foreground/[0.03] hover:bg-foreground/[0.05] border-foreground/10 flex min-w-0 flex-col gap-3 rounded-md border p-4 transition-colors"
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase',
            opportunity ? CATEGORY_TONE[idea.category!] : KIND_TONE[idea.kind as ReactiveKind]
          )}
        >
          {opportunity ? t(`categories.${idea.category}`) : t(`kinds.${idea.kind}`)}
        </span>
        {opportunity && (
          <span className="text-foreground/50 text-[10px] tracking-wide uppercase">
            {t(`kinds.${idea.kind}`)}
          </span>
        )}
        <span className="text-foreground/40 ml-auto flex min-w-0 items-center gap-1.5 truncate text-[11px]">
          <CryptoIcon symbol={idea.asset} className="size-3.5 shrink-0" />
          <span className="truncate">{idea.route === 'arb' ? idea.asset : idea.symbol}</span>
          {idea.route !== 'arb' && (
            <span className="hidden sm:inline">· {getExchangeLabel(idea.exchange)}</span>
          )}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-foreground text-base leading-6 font-medium tracking-tight">
          {t(`titles.${idea.kind}`, values)}
        </h3>
        <p className="text-foreground/55 text-xs leading-5">{t(reasonKey(idea), values)}</p>
      </div>
      {idea.relevance && opportunity && (
        <p className="text-shell-accent -mt-1 text-[11px]">
          {t(`relevance.${idea.relevance}`, { asset: idea.asset })}
        </p>
      )}
      <p className="text-foreground/40 -mt-1 text-[11px] leading-4">
        <span className="text-foreground/55">{t('skipIfLabel')}</span> {t(`skipIf.${idea.kind}`)}
      </p>
      {idea.venueFunding ? (
        <VenueFundingBars idea={idea} />
      ) : (
        <HomePriceChart symbol={idea.symbol} exchange={idea.exchange} levels={levels} />
      )}
      {opportunity && idea.route !== 'arb' && (
        <p className="text-foreground/70 text-[11px] leading-4">{t('tradeLine', values)}</p>
      )}
      {idea.orderPrompt && (
        <code className="bg-foreground/5 text-foreground/75 truncate rounded-sm px-2 py-1 font-mono text-[11px]">
          {idea.orderPrompt}
        </code>
      )}
      {idea.preview && <IdeaPreview idea={idea} />}
      {!opportunity && (
        <div className="border-foreground/10 flex min-h-16 flex-col gap-1 border-t pt-3">
          <span className="text-shell-accent flex items-center gap-1.5 text-[11px] font-medium">
            <SparkleIcon className="size-3" />
            {t('aiReasoning')}
          </span>
          {reasoning ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Response className="text-foreground/75 text-xs leading-5 [&_p]:mb-1">
                {reasoning}
              </Response>
            </motion.div>
          ) : brief.ai.status === 'running' ? (
            <motion.span
              className="bg-foreground/10 h-3 w-4/5 rounded-full"
              animate={{ opacity: [0.35, 0.8, 0.35] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            />
          ) : brief.ai.status === 'done' ? (
            // The answer didn't single this idea out; it may still be covered in the full brief.
            <button
              type="button"
              onClick={() => brief.setFullBriefOpen(true)}
              className="text-foreground/45 hover:text-foreground cursor-pointer text-left text-xs transition-colors"
            >
              {t('aiInFull')}
            </button>
          ) : (
            <button
              type="button"
              onClick={brief.writeBrief}
              className="text-foreground/45 hover:text-foreground cursor-pointer text-left text-xs transition-colors"
            >
              {t('aiPending')}
            </button>
          )}
        </div>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {idea.route === 'hedge' ? (
          <Button size="sm" className="flex-1" onClick={() => router.push(getHedgePagePath())}>
            <TrendIcon />
            {t('openHedge')}
          </Button>
        ) : idea.route === 'arb' && idea.arb ? (
          <Button
            size="sm"
            className="flex-1"
            onClick={() =>
              openArb({
                leg1: { accountId: idea.arb!.short.accountId, symbol: idea.arb!.short.symbol },
                leg2: { accountId: idea.arb!.long.accountId, symbol: idea.arb!.long.symbol },
              })
            }
          >
            <ShuffleIcon className="size-3.5" />
            {t('openArb')}
          </Button>
        ) : (
          <Button
            size="sm"
            className="flex-1"
            onClick={() => idea.orderPrompt && startTradePrompt(idea.orderPrompt)}
          >
            {t('review')}
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            startResearchPrompt(
              `Should I do this now? ${describeIdea(idea)}. Explain the trade-offs simply, including what would prove it wrong.`
            )
          }
        >
          <SparkleIcon />
          {t('ask')}
        </Button>
      </div>
    </motion.li>
  )
}

/**
 * Ideas for you, in Alex and John's two buckets:
 * - Reactive — something changed, adjust what you hold: cut a position a normal move could
 *   liquidate, bank part of a winner, stop a loser, close an expiring option, hedge a
 *   concentrated book, stop paying heavy funding.
 * - Opportunities — new positions from the playbook (momentum, mean reversion, arbitrage,
 *   fundamental, news) on the coins the user trades, watches and holds.
 * Each with its chart, the rule that raised it, why it's shown to this user, and the order or
 * builder it would take — opened for review, never sent from Home.
 */
export function HomeIdeasPanel({
  brief,
  opportunities,
  opportunitiesPending,
  opportunitiesAllowed,
  className,
}: {
  brief: HomeDailyBrief
  opportunities: readonly TradeIdea[]
  opportunitiesPending: boolean
  opportunitiesAllowed: boolean
  className?: string
}) {
  const t = useTranslations('trade.home.ideas')
  const reactive = brief.ideas.filter((idea) => idea.bucket === 'reactive')
  // The "first trade" nudge only fills an empty Opportunities tab.
  const fallback = brief.ideas.filter((idea) => idea.bucket === 'opportunity')
  const shownOpportunities = opportunities.length > 0 ? opportunities : fallback
  const [picked, setPicked] = useState<IdeaBucket | null>(null)
  const bucket: IdeaBucket = picked ?? (reactive.length > 0 ? 'reactive' : 'opportunity')
  const [playbookOpen, setPlaybookOpen] = useState(false)
  const ideas = bucket === 'reactive' ? reactive : shownOpportunities
  const pending = !brief.ready || (bucket === 'opportunity' && opportunitiesPending)

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          <SegmentedControl
            size="xs"
            value={bucket}
            onChange={setPicked}
            options={[
              { value: 'reactive', label: t('buckets.reactive', { count: reactive.length }) },
              {
                value: 'opportunity',
                label: t('buckets.opportunity', { count: opportunities.length }),
              },
            ]}
          />
          <Button size="xs" variant="ghost" onClick={() => setPlaybookOpen(true)}>
            <BookOpenIcon className="size-3" />
            {t('playbook.button')}
          </Button>
        </>
      }
    >
      <p className="text-foreground/50 -mt-1 pb-3 text-xs">{t(`bucketHelp.${bucket}`)}</p>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={bucket}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: HOME_EASE }}
        >
          {pending ? (
            <div
              className="grid grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-2"
              aria-busy="true"
            >
              {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-[26rem] w-full" />
              ))}
            </div>
          ) : bucket === 'opportunity' && !opportunitiesAllowed && fallback.length === 0 ? (
            <p className="text-foreground/50 py-6 text-sm">{t('empty.locked')}</p>
          ) : ideas.length === 0 ? (
            <p className="text-foreground/50 py-6 text-sm">{t(`empty.${bucket}`)}</p>
          ) : (
            <motion.ul
              variants={HOME_LIST}
              initial="hidden"
              animate="show"
              className="grid grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-2"
            >
              {ideas.map((idea) => (
                <IdeaCard key={idea.id} idea={idea} brief={brief} />
              ))}
            </motion.ul>
          )}
        </motion.div>
      </AnimatePresence>
      <p className="text-foreground/35 pt-3 text-[11px]">{t('disclaimer')}</p>
      <HomePlaybookDialog
        open={playbookOpen}
        onOpenChange={setPlaybookOpen}
        live={liveSetups(opportunities)}
      />
    </HomePanel>
  )
}
